import { cx } from '@linaria/core';
import { memo, useCallback, useRef, useState } from 'react';
import { ListType, SettingsType } from '../../../constants/enums';
import { locales } from '../../../locales/en';
import BouncingDots from '../../common/loading/BouncingDots';
import Popover from '../../common/Popover';
import { useList } from '../../Hooks/useList';
import { useModelsLoadedAppState } from '../../Hooks/useModelsLoadedAppState';
import { useSettingSelector } from '../../Hooks/useSettings';
import { PAPER_HEIGHTS } from '../../theme';
import QuickAccess from '../Modals/Drawers/QuickAccess';
import "./ModelCountPill.linaria.module.css!=!./ModelCountPill?./ModelCountPill.linaria.module.css?css=H4sIAAAAAAAAA3WU3Y6bMBCF7/sUlapKW6leAYGQ0Pt9Dct/GINjs7aBEKvvXiX8mlVvkI5mDpgz3/gduRuUGpaOC5m8B8q3iFKhOHC6LS7t/c/fb1MDC9rZsR1r5/Rt7+hHGA/UmTT3g6CuKpIk2qpXC2N3H0QvPRW2lWgsSsnWcgXj6qzr7hEqj6TgCgjHbrYgTDlmFge5w3hMUy1Lz1FbpNuncgnj8+kimPJES22KHx8fH0sRGRjjNtUNOUj/PA6gwjDihFYF0bK7qcXGTzC+4l63+iC9FIqBigleuSLe/TCxMC6H6PTgB+lLrRwYJkceRavhAg3VdXcJ1dRuxYMV1yBsbWIs+Zx1mmy1kgRhbtLXnXWiHAHRyjHlDonyTzhWaYTLUPkbMlyoZeJJOHFtP3XSz6eIo+jn/hQjTpVsD/LLKWyLCAOYuYGxNfCEQqwFbWyoPEak4UZ3ioJpuD0ybwCUUiM3YawlRgZsfb+WV2Y1jB3hdcY91oYyAwyiorNFsoPnWkMnGxZJTzpjtSlaLfYhSQ3jdEzysTvIYJGyYJECA/timIPde5o9CqtaHZKVbk+DlEG/PPabF2s7A7kEOG9yR9seZn6CVDaXex+qEP40gF+JXEY8VAH65w3958beqqHBdN5YwzF6S7Ls9/ftEb1fsnWWfQ/jzysh/OGHSjgGXhAVSg8GtbtZ5idEh3qZJWUl6qRb6jVegbgJBeZbKw0AF8ng0nTm+7yVWLKU5t/f1Z6gWYsFrQ6gZdt6vIhOEpuH6n98ox65F9WUM6DVM/c1C4yhoG3J01BNF5qtjFBNsUZdRcu1eZDLmk+sTCH8A/o0Fbc5BgAA"
const quickAccessStyles = "atm_lo_ftgil2 atm_le_ftgil2";
const popoverStyles = "atm_vy_1wdtr47";
const containerStyles = "atm_9s_1txwivl atm_h_1h6ojuz atm_cx_1y44olf";
const stylesIntegratedRoot = "atm_7l_1638ien atm_ar_1bp4okc atm_h_1h6ojuz atm_g3_19bvopo atm_cs_1fw03zg atm_9s_1txwivl atm_c8_rdoju8 atm_vy_or1blg";
const stylesIntegrated = "atm_fc_1h6ojuz atm_h_1h6ojuz atm_gq_yh40bf atm_9s_1txwivl atm_vy_1osqo2v";
const stylesIntegratedCount = "atm_fc_1yb4nlp";
const stylesIntegratedLabel = "atm_h_1h6ojuz atm_9s_1txwivl";
const pillStyles = "atm_2d_boidks atm_5j_1tcgj5g atm_9j_tlke0l atm_lo_14y27yu atm_le_14y27yu atm_lk_rdoju8 atm_ll_rdoju8";
const countTextStyles = "atm_c8_19bvopo atm_g3_dlk8xv atm_cs_ni7l0g atm_7l_1mhwkbd atm_vv_1q9ccgz";

// the quick-access popover is fed the count, so the pill is inert while
// the count is still on its way
const pillLoadingStyles = "atm_9j_73adwj";

// about the width of a four-digit count, so the pill does not resize when
// the number lands
const loadingDotsStyles = "atm_jb_1tcgj5g";
const miniIndicatorStyles = "atm_vy_i2wt44 atm_e2_i2wt44 atm_5j_1ssbidh atm_2d_bo22s7 atm_bb_idpfg4";
const stylesMarginRight = "atm_h0_1y44olf";
interface ModelCountPillProps {
  integrated?: boolean;
  onClear?(): void;
}
const ModelCountPill = memo(({
  integrated,
  onClear
}: ModelCountPillProps) => {
  const anchorEl = useRef(null);
  const [showQuickAccess, setShowQuickAccess] = useState(false);
  const broadcastingModels = useList(ListType.BROADCASTING_MODELS, {
    returnIDs: true
  });
  const displayedModels = useList(ListType.DEFAULT, {
    returnIDs: true
  });
  const [show] = useSettingSelector(SettingsType.DEFAULT, 'show');
  const [regions] = useSettingSelector(SettingsType.DEFAULT, 'regions');
  // a seeded boot lists only the server's first page (100 desktop, 20
  // mobile) until the first CAMS table lands, so the count would read
  // falsely low; show the dots until the live list is in
  const modelsLoaded = useModelsLoadedAppState();
  const broadcastingCount = broadcastingModels.length;
  const displayedCount = displayedModels.length;
  const showFilterActive = show !== 'custom';
  const regionFilterActive = Object.values(regions).some(region => region.show === false);
  const modelCount = showFilterActive || regionFilterActive ? displayedCount : broadcastingCount;
  const modelCountString = showFilterActive || regionFilterActive ? `${displayedCount}/${broadcastingCount}` : `${broadcastingCount}`;
  const onToggleTooltip = useCallback(() => {
    setShowQuickAccess(prev => !prev);
  }, []);
  if (broadcastingCount === 0) {
    return null;
  }
  const count = modelsLoaded ? modelCountString : <BouncingDots label={locales.header.loadingModelCount} className={loadingDotsStyles} size={3} />;
  if (integrated) {
    const isCountStringLarge = modelsLoaded && modelCountString.length > 6;
    return <div data-testid="Container__Integrated__ModelCountPill" className={stylesIntegratedRoot}>
                <div className={stylesIntegrated}>
                    {!isCountStringLarge && <div className={cx(miniIndicatorStyles, stylesMarginRight)} />}
                    <div data-testid="ModelCountPill__Count" className={stylesIntegratedCount}>
                        {count}
                    </div>
                </div>
                <div className={cx(isCountStringLarge && stylesIntegratedLabel)}>
                    {isCountStringLarge && <div className={cx(miniIndicatorStyles, stylesMarginRight)} />}
                    Models
                </div>
            </div>;
  }
  return <>
            <div ref={anchorEl} className={cx(containerStyles, pillStyles, !modelsLoaded && pillLoadingStyles)} onClick={modelsLoaded ? onToggleTooltip : undefined} data-testid="Container__ModelCountPill">
                <div className={miniIndicatorStyles} />
                <span data-testid="ModelCountPill__Count" className={countTextStyles}>
                    {count} models
                </span>
            </div>
            <Popover anchorEl={anchorEl.current} open={showQuickAccess} onClose={onToggleTooltip} className={popoverStyles} zIndex={PAPER_HEIGHTS.drawer - 1}>
                <div className={quickAccessStyles}>
                    <QuickAccess modelCount={modelCount} onClear={onClear} />
                </div>
            </Popover>
        </>;
});
export default ModelCountPill;